<template>
  <div class="p-3 bg-secondary-1">
    <validation-observer ref="formValidator">
      <div class="row">
        <div class="col">
          <div class="row mx-1">
            <div
              v-for="filter in mainFilters"
              :key="filter.name"
              :class="`${filter.class || 'col'} col-sm my-1`"
            >
              <validation-provider
                v-slot="{ errors }"
                :rules="filter.validations"
              >
                <component
                  :is="filter.component"
                  v-model="value[filter.name]"
                  :class-container="filter.classContainer"
                  :clearable="filter.clearable"
                  :custom-error-msg.sync="
                    filter.error && errors[0] ? filter.error : errors[0]
                  "
                  :disabled="disabled(filter)"
                  :error="filter.error && errors[0] ? filter.error : errors[0]"
                  :fieldkey="filter.fieldKey"
                  :fieldname="filter.fieldName"
                  :i="filter.icon"
                  :icon="filter.icon"
                  :is-inline="filter.isInline"
                  :label="filter.label"
                  :left-icon="filter.leftIcon"
                  :mask-type="filter.maskType"
                  :masked="filter.masked != undefined ? filter.masked : false"
                  :options="filter.options"
                  :placeholder="filter.placeholder"
                  :type="filter.type"
                  :variant="filter.variant"
                  @change="changeFilter(filter)"
                  @input="changeFilter(filter)"
                />
              </validation-provider>
            </div>
            <slot />
          </div>
        </div>
        <div v-if="searchText" class="col-md-3 col-lg-2 col-sm my-1">
          <div class="d-flex justify-content-md-end justify-content-sm-start">
            <action-button
              v-if="showAdvancedOptions"
              class="mx-3 pl-3"
              :class="{ transparent: !advanced }"
              i="fal fa-sliders-v"
              variant="orange"
              @click="advanced = !advanced"
            />

            <action-button
              :class="{ 'mx-3': !showAdvancedOptions }"
              :disabled="disabledSearch"
              :i="searchIcon"
              :text="searchText"
              variant="success"
              @click="search"
            />
          </div>
        </div>
      </div>

      <div v-if="advanced" class="col-md-12">
        <div class="row py-3">
          <div class="col-md-12">
            <span
              class="d-flex align-items-center color-gray-8 font-14"
              :class="$style.separator"
            >
              FILTROS
            </span>
          </div>
        </div>
      </div>

      <div v-if="advanced" class="row mx-1">
        <div v-if="$slots.header_advanced" class="col-12">
          <slot name="header_advanced" />
        </div>
        <div
          v-for="filter in advancedFilters"
          :key="filter.name"
          :class="`${filter.class || 'col'} col-sm my-1`"
        >
          <validation-provider v-slot="{ errors }" :rules="filter.validations">
            <component
              :is="filter.component"
              v-model="value[filter.name]"
              :class-container="filter.classContainer"
              :clearable="filter.clearable"
              :custom-error-msg.sync="
                filter.error && errors[0] ? filter.error : errors[0]
              "
              :disabled="disabled(filter)"
              :error="filter.error && errors[0] ? filter.error : errors[0]"
              :fieldkey="filter.fieldKey"
              :fieldname="filter.fieldName"
              :i="filter.icon"
              :icon="filter.icon"
              :is-inline="filter.isInline"
              :label="filter.label"
              :left-icon="filter.leftIcon"
              :mask-type="filter.maskType"
              :masked="filter.masked != undefined ? filter.masked : false"
              :name="
                filter.componentName != undefined ? filter.componentName : ''
              "
              :options="filter.options"
              :placeholder="filter.placeholder"
              :type="filter.type"
              :variant="filter.variant"
              @change="changeFilter(filter)"
              @input="changeFilter(filter)"
            />
          </validation-provider>
        </div>
        <slot name="advanced" />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// TODO: Cambiar ruta cuando los componentes se migren a shared
import CustomInput from '@/components/common/form/CustomInput';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import ActionButton from '@/components/common/ActionButton';
import CustomRadioGroup from '@/components/common/CustomRadio.vue';

export default {
  name: 'FilterManager',
  components: {
    ValidationObserver,
    ValidationProvider,
    CustomInput,
    CustomDropDown,
    ActionButton,
    CustomRadioGroup,
  },
  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    displayAdvanced: {
      type: Boolean,
      default: () => true,
    },
    openAdvanced: {
      type: Boolean,
      default: () => false,
    },
    disabledSearch: {
      type: Boolean,
      default: () => false,
    },
    searchText: {
      type: String,
      default: () => 'Buscar',
    },
    searchIcon: {
      type: String,
      default: () => 'fa fa-search',
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      advanced: false,
      showAdvancedOptions: this.displayAdvanced,
    };
  },
  computed: {
    filtersVisible() {
      return this.filters.filter(
        (filter) => filter.visible || filter.visible == null
      );
    },
    advancedFilters() {
      return this.filtersVisible.filter((filter) => filter.isAdvanced);
    },
    mainFilters() {
      return this.filtersVisible.filter(
        (filter) => !filter.isAdvanced || filter.isAdvanced == null
      );
    },
  },
  watch: {
    openAdvanced: {
      handler(newValue) {
        this.advanced = newValue;
      },
      immediate: true,
    },
  },
  mounted() {
    //Execute methods immediate
    this.filtersVisible.forEach((filter) => {
      if (filter.immediateOnChanged) {
        filter.onChanged(this.value[filter.name]);
      }
    });

    this.filters.forEach((filter) => {
      if (filter.clearModelsOnUpdate) {
        // is array
        this.$watch(`value.${filter.name}`, () => {
          filter.clearModelsOnUpdate.forEach((other) => {
            if (typeof other == 'string') {
              this.value[other] = null;
            } else if (typeof other == 'object') {
              this.value[other.name] = other.default;
            }
          });
        });
      }
    });
  },
  created() {
    //initialize every filter value
    this.filters.forEach((filter) => {
      this.$set(
        this.value,
        filter.name,
        this.value[filter.name] || filter.initialValue
      );
    });
  },
  methods: {
    async search(props = {}) {
      const isValid = await this.$refs.formValidator.validate();
      this.$emit('search', isValid ? { ...this.value, ...props } : null);
    },
    assignValue(key, value) {
      this.value[key] = value;
    },
    changeFilter(filter) {
      let data = filter.returnModel ? this.value : this.value[filter.name];

      if (filter.onChanged) {
        filter.onChanged(data);
      } else {
        this.$emit(filter.name, data);
      }
    },
    disabled(filter) {
      if (filter.disabled) {
        if (typeof filter.disabled == 'boolean') {
          return filter.disabled;
        }

        let isDisabled = filter.disabled.some((other) => {
          let filterConfig = this.filtersVisible.find((x) => x.name == other);
          return this.value[other] == null && filterConfig;
        });

        return isDisabled;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" module>
$marginSeparatorText: 1em;

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #aab7b1;
}
.separator::before {
  margin-right: $marginSeparatorText;
}
.separator::after {
  margin-left: $marginSeparatorText;
}
</style>
